/**
 * localStorage Service
 * Unified interface for localStorage operations with proper serialization
 */

const STORAGE_KEYS = {
  SESSION: 'vinpro_session',
  QUERY_HISTORY: 'vinpro_query_history'
};

const MAX_HISTORY_RECORDS = 100;

// Safe localStorage operations
const safeGet = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (e) {
    console.error('localStorage read error:', e);
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    console.error('localStorage write error:', e);
    return false;
  }
};

// Session Management
export const getSession = () => {
  return safeGet(STORAGE_KEYS.SESSION);
};

export const setSession = (sessionData) => {
  return safeSet(STORAGE_KEYS.SESSION, {
    ...sessionData,
    lastActivity: new Date().toISOString()
  });
};

export const updateSessionActivity = () => {
  const session = getSession();
  if (session) {
    session.lastActivity = new Date().toISOString();
    return safeSet(STORAGE_KEYS.SESSION, session);
  }
  return false;
};

export const clearSession = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.SESSION);
    return true;
  } catch (e) {
    console.error('localStorage remove error:', e);
    return false;
  }
};

export const isSessionValid = () => {
  const session = getSession();
  if (!session) return false;

  const loginTime = new Date(session.loginTime);
  const now = new Date();
  const hoursDiff = (now - loginTime) / (1000 * 60 * 60);

  // Session expires after 8 hours
  return hoursDiff < 8;
};

// Query History Management
export const getQueryHistory = () => {
  return safeGet(STORAGE_KEYS.QUERY_HISTORY) || [];
};

export const addQueryRecord = (record) => {
  const history = getQueryHistory();

  // Add new record at the beginning
  history.unshift({
    ...record,
    id: generateUUID(),
    timestamp: new Date().toISOString()
  });

  // Keep only the last MAX_HISTORY_RECORDS
  if (history.length > MAX_HISTORY_RECORDS) {
    history.splice(MAX_HISTORY_RECORDS);
  }

  return safeSet(STORAGE_KEYS.QUERY_HISTORY, history);
};

export const clearQueryHistory = () => {
  return safeSet(STORAGE_KEYS.QUERY_HISTORY, []);
};

// Get queries for today
export const getTodayQueries = () => {
  const history = getQueryHistory();
  const today = new Date().toISOString().split('T')[0];
  return history.filter(record => record.timestamp.startsWith(today));
};

// Get queries for last N days
export const getQueriesForDays = (days) => {
  const history = getQueryHistory();
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);

  return history.filter(record => new Date(record.timestamp) >= cutoffDate);
};

// Calculate dashboard metrics
export const calculateDashboardMetrics = () => {
  const todayQueries = getTodayQueries();
  const totalQueries = todayQueries.length;
  const successfulQueries = todayQueries.filter(q => q.status === 'success').length;

  return {
    dailyQueries: totalQueries,
    successRate: totalQueries > 0
      ? ((successfulQueries / totalQueries) * 100).toFixed(1)
      : '0.0',
    avgLatency: totalQueries > 0
      ? Math.round(todayQueries.reduce((sum, q) => sum + (q.latency || 0), 0) / totalQueries)
      : 0,
    totalCommercialValue: todayQueries.reduce((sum, q) => sum + (q.commercialValue || 0), 0)
  };
};

// Calculate top VINs
export const calculateTopVins = (limit = 5) => {
  const history = getQueryHistory();
  const vinCounts = {};

  history.forEach(record => {
    if (record.status === 'success' && record.vin) {
      if (!vinCounts[record.vin]) {
        vinCounts[record.vin] = {
          count: 0,
          vehicle: record.vehicle,
          lastQueried: record.timestamp
        };
      }
      vinCounts[record.vin].count++;
    }
  });

  return Object.entries(vinCounts)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, limit)
    .map(([vin, data]) => ({
      vin,
      brand: data.vehicle?.brand || 'Desconocido',
      model: data.vehicle?.model || 'Desconocido',
      year: data.vehicle?.year || 'N/A',
      consultations: data.count,
      lastQueried: data.lastQueried
    }));
};

// Get recent activities for dashboard
export const getRecentActivities = (limit = 10) => {
  const history = getQueryHistory();
  return history.slice(0, limit).map(record => ({
    id: record.id,
    type: 'vin_lookup',
    user: record.user,
    description: `Consulta VIN: ${record.vin}`,
    timestamp: new Date(record.timestamp),
    status: record.status,
    details: {
      vin: record.vin,
      vehicle: record.vehicle,
      ip: record.location?.ip,
      location: record.location?.city
    }
  }));
};

// Get daily query counts for charts (last 7 days)
export const getDailyQueryCounts = (days = 7) => {
  const history = getQueryHistory();
  const counts = {};

  // Initialize last N days with 0
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateKey = date.toISOString().split('T')[0];
    counts[dateKey] = { total: 0, success: 0, latency: [] };
  }

  // Count queries per day
  history.forEach(record => {
    const dateKey = record.timestamp.split('T')[0];
    if (counts[dateKey]) {
      counts[dateKey].total++;
      if (record.status === 'success') {
        counts[dateKey].success++;
      }
      if (record.latency) {
        counts[dateKey].latency.push(record.latency);
      }
    }
  });

  // Format for charts
  return Object.entries(counts).map(([date, data]) => {
    const [year, month, day] = date.split('-');
    return {
      fecha: `${day}/${month}`,
      consultas: data.total,
      tasa_exito: data.total > 0 ? ((data.success / data.total) * 100).toFixed(1) : 0,
      latencia: data.latency.length > 0
        ? Math.round(data.latency.reduce((a, b) => a + b, 0) / data.latency.length)
        : 0
    };
  });
};

// UUID Generator
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export default {
  getSession,
  setSession,
  updateSessionActivity,
  clearSession,
  isSessionValid,
  getQueryHistory,
  addQueryRecord,
  clearQueryHistory,
  getTodayQueries,
  getQueriesForDays,
  calculateDashboardMetrics,
  calculateTopVins,
  getRecentActivities,
  getDailyQueryCounts
};
